import { createSelector } from '@reduxjs/toolkit';
import { ReduxModel, ReportResponseModel } from 'core/models';
import { isValidArrayAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import { FilterType } from 'core/constants/filter';
import { IBuilderFilter } from 'core/models/report-builder/report-builder';
import { getBrokenCustomFilterExpressions } from 'core/utils/report-expressions';
import { IUpdatedReportBuilderInfo } from './report-builder-store.state';
import { computeValidDataSourceIdentifiers } from './report-builder-store.utils';

const reportBuilderSelector = ({ reportBuilder }: ReduxModel.IGlobalState) => reportBuilder;

const reportPayloadSelector = ({
  reportBuilder: {
    updatedReportBuilderInfo: { reportBuilderSavePayload } = {} as IUpdatedReportBuilderInfo,
  },
}: ReduxModel.IGlobalState) => reportBuilderSavePayload;

const requestProcessingSelector = ({
  reportBuilder: { requestProcessing },
}: ReduxModel.IGlobalState) => requestProcessing;

const reportBuilderInfoSelector = ({
  reportBuilder: {
    reportBuilderInfo,
  },
}: ReduxModel.IGlobalState) => reportBuilderInfo;

const assortedItemSelector = ({
  reportBuilder: { assortedInfo },
}: ReduxModel.IGlobalState) => assortedInfo;

const filterBuilderSelector = ({
  reportBuilder: { filterBuilder },
}: ReduxModel.IGlobalState) => filterBuilder;

const appliedFiltersSelector = ({
  reportBuilder: { filterBuilder: { appliedFilters } },
}: ReduxModel.IGlobalState) => appliedFilters;

const filterConfigSelector = ({
  reportBuilder: { filterBuilder: { filterConfig } },
}: ReduxModel.IGlobalState) => filterConfig;

const isUserModifiedSelector = ({
  reportBuilder: { filterBuilder: { isUsersModified } },
}: ReduxModel.IGlobalState) => isUsersModified;

const availableDimensionsSelector = ({
  reportBuilder: {
    updatedReportBuilderInfo: { appliedDimensions } = {} as IUpdatedReportBuilderInfo,
  },
}: ReduxModel.IGlobalState) => appliedDimensions;
const appliedMeasuresSelector = ({ reportBuilder: { updatedReportBuilderInfo: { appliedMeasures } } }: ReduxModel.IGlobalState) => appliedMeasures;

const comboConfigSelector = ({ reportBuilder: { updatedReportBuilderInfo: { reportBuilderSavePayload } } }: ReduxModel.IGlobalState): ReportResponseModel.IComboMeasureConfig[] => (reportBuilderSavePayload?.Visualization?.Builder as ReportResponseModel.IChartVisualizationBuilder)?.ComboConfig ?? [];

const comboYAxisColumnsSelector = ({ reportBuilder: { updatedReportBuilderInfo: { reportBuilderSavePayload } } }: ReduxModel.IGlobalState): string[] => (reportBuilderSavePayload?.Visualization?.Builder as ReportResponseModel.IChartVisualizationBuilder)?.YAxis?.Columns ?? [];
const comboXAxisColumnsSelector = ({ reportBuilder: { updatedReportBuilderInfo: { reportBuilderSavePayload } } }: ReduxModel.IGlobalState): string[] => (reportBuilderSavePayload?.Visualization?.Builder as ReportResponseModel.IChartVisualizationBuilder)?.XAxis?.Columns ?? [];
const sortStateSelector = ({ reportBuilder: { updatedReportBuilderInfo: { sortState } } }: ReduxModel.IGlobalState) => sortState;
const calculatedColumnsSel = ({ reportBuilder: { updatedReportBuilderInfo: { calculatedColumns } } }: ReduxModel.IGlobalState) => calculatedColumns;
const previewDataSelector = ({ reportBuilder: { previewData } }: ReduxModel.IGlobalState) => previewData;
const previewBakUpSelector = ({ reportBuilder: { previewBackUpInfo } }: ReduxModel.IGlobalState) => previewBackUpInfo;

const expressionStoreSelector = ({ reportBuilder: { updatedReportBuilderInfo: { expressionStore } } } : ReduxModel.IGlobalState) => expressionStore;

const combinedConfigLoadedSelector = ({ reportBuilder: { reportEditInfo: { combineConfigLoaded } } }: ReduxModel.IGlobalState) => combineConfigLoaded;
const isEditingSelector = ({ reportBuilder: { reportEditInfo: { isEditing } } }: ReduxModel.IGlobalState) => isEditing;
const drilldownConfigMappingSelector = ({ reportBuilder: { updatedReportBuilderInfo: { drilldownConfigInfo: { drilldownConfigMapping } } } }: ReduxModel.IGlobalState) => drilldownConfigMapping;
const drilldownConfigInfoSelector = ({ reportBuilder: { updatedReportBuilderInfo: { drilldownConfigInfo } } }: ReduxModel.IGlobalState) => drilldownConfigInfo;
const drilldownSortStateSelector = ({ reportBuilder: { updatedReportBuilderInfo: { drilldownConfigInfo: { drilldownColumnConfig: { sortState } } } } }: ReduxModel.IGlobalState) => sortState;
const drilldownEnabledStateSelector = ({ reportBuilder: { updatedReportBuilderInfo: { drilldownConfigInfo: { enableDrilldown } } } }: ReduxModel.IGlobalState) => enableDrilldown;
const drilldownPaginationSelector = ({ reportBuilder: { updatedReportBuilderInfo: { drilldownConfigInfo: { drilldownPagination } } } }: ReduxModel.IGlobalState) => drilldownPagination;
const fieldSelectors = ({ reportBuilder: { reportBuilderInfo: { fields } } }: ReduxModel.IGlobalState) => fields;
const customFilterDefinitionsSelector = ({ reportBuilder: { filterBuilder: { customFilterDefinitions } } }: ReduxModel.IGlobalState) => customFilterDefinitions;
// UseVar=true definitions only — the custom filters usable as measure-expression variables
// (custom-filter-expressions feature). Memoized so the IbexEditor's lexer/autocomplete deps are stable.
const customFilterVariablesSelector = createSelector(
  customFilterDefinitionsSelector,
  (definitions) => (definitions || []).filter((definition) => definition.UseVar),
);

const selectEditedExpressions = createSelector(expressionStoreSelector,
  (exprStore) => Object.keys(exprStore));

const calculatedColumnsSelector = ({ reportBuilder: { updatedReportBuilderInfo: { selectedTemplate: { ReportConfig: { CalculatedColumns } } } } }: ReduxModel.IGlobalState) => CalculatedColumns;
const builderMeasuresListSelector = ({ reportBuilder: { updatedReportBuilderInfo: { reportBuilderSavePayload } } }: ReduxModel.IGlobalState) => {
  const builderMeasures = reportBuilderSavePayload.Builder[0]?.Select?.Measures || [];
  const subBuilderMeasures = isValidArrayAndNotEmpty(reportBuilderSavePayload?.SubBuilders) ? reportBuilderSavePayload?.SubBuilders[0]?.Select?.Measures : [];
  return [...builderMeasures, ...subBuilderMeasures || []];
}; // TODO: subBuilders can be multiple items

const selectedReportConfig = ({ reportBuilder: { updatedReportBuilderInfo: { selectedTemplate: { ReportConfig } } } }: ReduxModel.IGlobalState) => ReportConfig;

const reportBuilderDynamicUserFields = ({ reportBuilder: { reportBuilderInfo: { additionalUserFields } } }: ReduxModel.IGlobalState) => additionalUserFields;

const dataSourceInfoSelector = ({ reportBuilder: { dataSourceInfo } }: ReduxModel.IGlobalState) => dataSourceInfo;

const dataSourceSidetrayPanelStateSelector = ({ reportBuilder: { assortedInfo: { isDataSourceSidetrayExpanded } } }: ReduxModel.IGlobalState) => isDataSourceSidetrayExpanded;

const nestedQueryFormStateSelector = ({ reportBuilder: { assortedInfo: { isNestedQueryFormOpen } } } : ReduxModel.IGlobalState) => isNestedQueryFormOpen;

const subQuerySelectedQueryData = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData } },
}: ReduxModel.IGlobalState) => SelectedQueryData;

const subQueryfilterConfigSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { filterBuilder: { filterConfig } } } } },
}: ReduxModel.IGlobalState) => filterConfig;

const subQueryfilterBuilderSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { filterBuilder } } } },
}: ReduxModel.IGlobalState) => filterBuilder;

const subQueryAppliedDimensionsSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { AppliedDimensions } } } },
}: ReduxModel.IGlobalState) => AppliedDimensions;

const subQueryAdditionalFiltersSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { additionalData: { AdditionalFilters } } } },
}: ReduxModel.IGlobalState) => AdditionalFilters;

const subQueryAdditionalUserFiltersSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { additionalData: { AdditionalUserFieldFilters } } } },
}: ReduxModel.IGlobalState) => AdditionalUserFieldFilters;

const subQueryAdditionalMeasuresSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { additionalData: { AdditionalMeasures } } } },
}: ReduxModel.IGlobalState) => AdditionalMeasures;

const subQueryAppliedMeasuresSelector = ({ reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { AppliedMeasures } } } } }: ReduxModel.IGlobalState) => AppliedMeasures;
const subQueryExpressionStoreSelector = ({ reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { ExpressionStore } } } } } : ReduxModel.IGlobalState) => ExpressionStore;

// Main-report measure/dimension expressions that reference a custom filter variable which is no longer
// valid — the definition was deleted, or its UseVar flag was unchecked (valid = UseVar=true definition
// IDs). Powers the "broken expression" badge + save gate (custom-filter-expressions feature). Memoized:
// recomputes only when the main expression store or the UseVar def set change by reference; per-tree
// walks are themselves cached by jsonExpression reference (see getBrokenCustomFilterExpressions).
const brokenCustomFilterExpressionsSelector = createSelector(
  expressionStoreSelector,
  customFilterVariablesSelector,
  (expressionStore, useVarDefinitions) => {
    const validVariableIds = new Set((useVarDefinitions || []).map((definition) => definition.ID));
    return getBrokenCustomFilterExpressions(expressionStore, validVariableIds);
  },
);

const subQueryBuilderTypeSelector = ({ reportBuilder: { reportQueryBuilder: { SelectedQueryData: { Type } } } } : ReduxModel.IGlobalState) => Type;
const subQueryDataSourceMappingSelector = ({
  reportBuilder: { reportQueryBuilder: { DataSourceMapping } },
}: ReduxModel.IGlobalState) => DataSourceMapping;

const subQueryDataSourceMappingPayloadSelector = ({
  reportBuilder: { reportQueryBuilder: { DataSourceMappingPayload } },
}: ReduxModel.IGlobalState) => DataSourceMappingPayload;

const selectedSubQuerySelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQuery } },
}: ReduxModel.IGlobalState) => SelectedQuery;

const queryListSelector = ({
  reportBuilder: { reportQueryBuilder: { DynamicQueryList } },
}: ReduxModel.IGlobalState) => DynamicQueryList;

const allSubBuilderAppliedFiltersSelector = createSelector(
  queryListSelector,
  (dynamicQueryList): Array<ReportResponseModel.IFilterConfig> => {
    if (!isValidArrayAndNotEmpty(dynamicQueryList)) return [];
    const seenFilterIds = new Set<string>();
    const appliedFilters: Array<ReportResponseModel.IFilterConfig> = [];
    dynamicQueryList.forEach((query) => {
      const queryFilters = query.QueryPayload?.Filter?.FilterGroup;
      if (!isValidArrayAndNotEmpty(queryFilters)) return;
      queryFilters.forEach((queryFilter) => {
        if (seenFilterIds.has(`${query.Id}:${queryFilter.ID}`)) return;
        seenFilterIds.add(`${query.Id}:${queryFilter.ID}`);
        const {
          Label, Type, DefaultValue, Metadata, DropdownMetaData: renderDropdownMetaData,
        } = queryFilter.Render || {};
        appliedFilters.push({
          ...queryFilter,
          Label,
          Type,
          DefaultValue,
          Metadata,
          Render: queryFilter.Render,
          QueryId: query.Id,
          DropdownMetaData: queryFilter.DropdownMetaData || renderDropdownMetaData,
          IsDynamic: true,
        });
      });
    });
    return appliedFilters;
  },
);

const breadcrumbDataSelector = ({
  reportBuilder: { reportQueryBuilder: { BreadcrumbData } },
}: ReduxModel.IGlobalState) => BreadcrumbData;

const queryPreviewDataSelector = ({
  reportBuilder: { reportQueryBuilder: { PreviewData } },
}: ReduxModel.IGlobalState) => PreviewData;

const queryBuilderDataSelector = ({
  reportBuilder: { reportQueryBuilder },
}: ReduxModel.IGlobalState) => reportQueryBuilder;

const dataSourceStateSelector = ({
  reportBuilder: { assortedInfo: { isDataSourceEditMode } },
}: ReduxModel.IGlobalState) => isDataSourceEditMode;

const subQueryActivePanelSelector = ({
  reportBuilder: { assortedInfo: { activeSubQueryPanel } },
}: ReduxModel.IGlobalState) => activeSubQueryPanel;

const subQuerySortStateSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { sortState } } } },
}: ReduxModel.IGlobalState) => sortState;

const subQueryPagingStateSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { Paging } } } },
}: ReduxModel.IGlobalState) => Paging;

const reportBuilderFeatureContextSelector = ({
  reportBuilder: { updatedReportBuilderInfo: { selectedTemplate: { ReportConfig: { FeatureContext } } } },
}: ReduxModel.IGlobalState) => FeatureContext;

const reportBuilderUserContextSelector = ({
  reportBuilder: { updatedReportBuilderInfo: { selectedTemplate: { ReportConfig } } },
}: ReduxModel.IGlobalState) => ReportConfig?.UserContext;

const drilldownMeasuresSidetrayPanelStateSelector = ({ reportBuilder: { assortedInfo: { isDrilldownMeasuresPanelExpanded } } }: ReduxModel.IGlobalState) => isDrilldownMeasuresPanelExpanded;

const drilldownFieldsSidetrayPanelStateSelector = ({ reportBuilder: { assortedInfo: { isDrilldownFieldsPanelExpanded } } }: ReduxModel.IGlobalState) => isDrilldownFieldsPanelExpanded;

const reportDataSourceSaveStateSelector = ({ reportBuilder: { assortedInfo: { isReportDataSourceSaved } } }: ReduxModel.IGlobalState) => isReportDataSourceSaved;

const subQueryFieldsSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { additionalData: { Fields } } } },
}: ReduxModel.IGlobalState) => Fields;

const subQueryFilterIsUsersModifiedSelector = ({
  reportBuilder: { reportQueryBuilder: { SelectedQueryData: { updatedInfo: { filterBuilder: { isUsersModified } } } } },
}: ReduxModel.IGlobalState) => isUsersModified;

// entity grouping selectors
const reportConfigIdSelector = ({
  reportBuilder: {
    reportEditInfo: { temporaryReportId },
    assortedInfo: { reportId },
  },
}: ReduxModel.IGlobalState) => temporaryReportId || reportId; // fallback to original report id if temporary id is not available
const entityGroupStoreSelector = ({ reportBuilder: { updatedReportBuilderInfo: { entityGroupStore } } } : ReduxModel.IGlobalState) => entityGroupStore;
const metaFieldOptionsSelector = ({ reportBuilder: { updatedReportBuilderInfo: { entityGroupOptionsMap } } }: ReduxModel.IGlobalState) => entityGroupOptionsMap;

const allReportsForLinkedInsightsSelector = ({
  reportBuilder: { reportBuilderInfo: { allReports } },
}: ReduxModel.IGlobalState) => allReports;

const reportConfigMappingSelector = ({
  reportBuilder: { reportBuilderInfo: { reportConfigMapping } },
}: ReduxModel.IGlobalState) => reportConfigMapping;

const linkedReportsConfigSelector = ({
  reportBuilder: { updatedReportBuilderInfo: { linkedReportsConfig } },
}: ReduxModel.IGlobalState) => linkedReportsConfig;

// Conditional Formatting Selectors
const conditionalFormattingSelector = ({
  reportBuilder: {
    updatedReportBuilderInfo: { conditionalFormatting },
  },
}: ReduxModel.IGlobalState) => conditionalFormatting;

const conditionalFormattingInPayloadSelector = ({
  reportBuilder: {
    updatedReportBuilderInfo: { reportBuilderSavePayload },
  },
}: ReduxModel.IGlobalState) => reportBuilderSavePayload?.Visualization?.ConditionalFormatting ?? { Enabled: true, Rules: [] };

const isEditingOrAddingFormattingRuleSelector = ({
  reportBuilder: {
    updatedReportBuilderInfo: { isCurrentlyEditingOrAddingFormattingRule },
  },
}: ReduxModel.IGlobalState) => isCurrentlyEditingOrAddingFormattingRule;

// Memoised view of the report's currently-valid data-source identifiers,
// derived from DataSourceMapping. Used by CustomFilterEditor to drop stale
// sub-builder filters whose query is no longer reachable from ROOT_QUERY
// after a data-source change.
const validDataSourceIdentifiersSelector = createSelector(
  subQueryDataSourceMappingSelector,
  (dataSourceMapping) => computeValidDataSourceIdentifiers(dataSourceMapping),
);

const countShowInMainFromBuilderFilter = (filter: IBuilderFilter | null | undefined): number => {
  if (!filter) return 0;
  const groupCount = filter.FilterGroup?.filter(
    (item) => item?.Render?.ShowInMainReport && item?.Render?.Type !== FilterType.PRE_APPLIED,
  ).length ?? 0;
  const subCount = filter.SubFilters?.reduce(
    (acc, sub) => acc + countShowInMainFromBuilderFilter(sub), 0,
  ) ?? 0;
  return groupCount + subCount;
};

const mainReportFilterCountSelector = ({
  reportBuilder: {
    filterBuilder: { filterConfig: mainFilterConfig, appliedFilters },
    reportQueryBuilder: {
      SelectedQuery: selectedQueryId,
      SelectedQueryData: { updatedInfo: { filterBuilder: { filterConfig: currentSubQueryFilterConfig } } },
      DynamicQueryList,
    },
  },
}: ReduxModel.IGlobalState): number => {
  const nativeMainFilterCount = mainFilterConfig?.filter(
    (config) => !config.QueryId
      && (config.IsCustomFilter || (!config.AssociatedCustomFilterId && appliedFilters[config.ID]))
      && !(config.IsDynamic && config.IsRemoved)
      && config.Type !== FilterType.PRE_APPLIED,
  ).length ?? 0;

  const currentQueryShowInMainCount = currentSubQueryFilterConfig?.filter(
    (config) => config?.Render?.ShowInMainReport
      && config.Type !== FilterType.PRE_APPLIED
      && !config.IsRemoved,
  ).length ?? 0;

  const otherQueriesShowInMainCount = DynamicQueryList?.reduce((count, query) => {
    if (query.Id === selectedQueryId) return count;

    if (query.QueryDataInfo) {
      return count + (query.QueryDataInfo.updatedInfo?.filterBuilder?.filterConfig?.filter(
        (config) => config?.Render?.ShowInMainReport
          && config.Type !== FilterType.PRE_APPLIED
          && !config.IsRemoved,
      ).length ?? 0);
    }

    return count + countShowInMainFromBuilderFilter(query.QueryPayload?.Filter);
  }, 0) ?? 0;

  return nativeMainFilterCount + currentQueryShowInMainCount + otherQueriesShowInMainCount;
};

export {
  reportBuilderSelector,
  requestProcessingSelector,
  reportPayloadSelector,
  assortedItemSelector,
  reportBuilderInfoSelector,
  availableDimensionsSelector,
  appliedMeasuresSelector,
  filterBuilderSelector,
  sortStateSelector,
  previewDataSelector,
  previewBakUpSelector,
  appliedFiltersSelector,
  filterConfigSelector,
  isUserModifiedSelector,
  calculatedColumnsSel,
  expressionStoreSelector,
  combinedConfigLoadedSelector,
  isEditingSelector,
  drilldownConfigMappingSelector,
  drilldownConfigInfoSelector,
  drilldownSortStateSelector,
  drilldownEnabledStateSelector,
  drilldownPaginationSelector,
  fieldSelectors,
  customFilterVariablesSelector,
  brokenCustomFilterExpressionsSelector,
  calculatedColumnsSelector,
  builderMeasuresListSelector,
  selectedReportConfig,
  reportBuilderDynamicUserFields,
  dataSourceInfoSelector,
  dataSourceSidetrayPanelStateSelector,
  nestedQueryFormStateSelector,
  subQueryfilterConfigSelector,
  subQueryfilterBuilderSelector,
  subQueryAppliedDimensionsSelector,
  subQueryAdditionalFiltersSelector,
  subQueryAdditionalUserFiltersSelector,
  subQueryAppliedMeasuresSelector,
  subQueryDataSourceMappingSelector,
  selectedSubQuerySelector,
  subQueryExpressionStoreSelector,
  queryListSelector,
  breadcrumbDataSelector,
  queryPreviewDataSelector,
  queryBuilderDataSelector,
  dataSourceStateSelector,
  subQueryActivePanelSelector,
  reportBuilderFeatureContextSelector,
  reportBuilderUserContextSelector,
  subQueryAdditionalMeasuresSelector,
  subQueryDataSourceMappingPayloadSelector,
  drilldownMeasuresSidetrayPanelStateSelector,
  drilldownFieldsSidetrayPanelStateSelector,
  subQuerySortStateSelector,
  subQueryPagingStateSelector,
  reportDataSourceSaveStateSelector,
  subQueryFieldsSelector,
  subQuerySelectedQueryData,
  subQueryFilterIsUsersModifiedSelector,
  reportConfigIdSelector,
  entityGroupStoreSelector,
  metaFieldOptionsSelector,
  subQueryBuilderTypeSelector,
  conditionalFormattingSelector,
  isEditingOrAddingFormattingRuleSelector,
  conditionalFormattingInPayloadSelector,
  allReportsForLinkedInsightsSelector,
  reportConfigMappingSelector,
  linkedReportsConfigSelector,
  allSubBuilderAppliedFiltersSelector,
  validDataSourceIdentifiersSelector,
  mainReportFilterCountSelector,
  selectEditedExpressions,
  comboConfigSelector,
  comboYAxisColumnsSelector,
  comboXAxisColumnsSelector,
};
